import { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useCoreLoadingQuery, OnlineStateChangeHandler, LoadingQueryOptions } from '@heltti/common';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useNetworkServiceState } from '~/contexts/network-service-state';
import * as ErrorDiagnostics from '~/error';
import { usePreviousValue } from '~/hooks/previous-value';
import { isTransientNetworkError } from '~/utils/network-error';

export function useLoadingQuery<
    TData,
    TVariables extends OperationVariables = OperationVariables,
    TSubscriptionData = TData,
    TSubscriptionVariables extends OperationVariables = OperationVariables
>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    // eslint-disable-next-line @typescript-eslint/ban-types
    ...[options]: {} extends TVariables
        ? [options?: LoadingQueryOptions<TData, TVariables, TSubscriptionData, TSubscriptionVariables>]
        : [options: LoadingQueryOptions<TData, TVariables, TSubscriptionData, TSubscriptionVariables>]
) {
    const { reachability } = useNetworkServiceState();

    const networkBecameAvailable =
        usePreviousValue(reachability) === 'service-unavailable' && reachability !== 'service-unavailable';
    const networkBecameUnavailable =
        usePreviousValue(reachability) !== 'service-unavailable' && reachability === 'service-unavailable';

    const onlineEventHandler = useRef<OnlineStateChangeHandler | null>(null);

    /**
     * Add online event listener
     */
    const addOnlineEventListener = useCallback((handler: OnlineStateChangeHandler) => {
        onlineEventHandler.current = handler;
    }, []);

    /**
     * Remove online event listener
     */
    const removeOnlineEventListener = useCallback((handler: OnlineStateChangeHandler) => {
        onlineEventHandler.current = null;
    }, []);

    /**
     * Handle online state change
     */
    useEffect(() => {
        if (networkBecameAvailable) {
            onlineEventHandler.current?.(true);
        } else if (networkBecameUnavailable) {
            onlineEventHandler.current?.(false);
        }
    }, [networkBecameAvailable, networkBecameUnavailable]);

    const handleQueryError = useMemo(
        () => (error: unknown) => {
            if (isTransientNetworkError(error)) {
                ErrorDiagnostics.log(`Subscription transient error (suppressed): ${(error as Error).message}`);
                return;
            }
            ErrorDiagnostics.error(error);
        },
        []
    );

    return useCoreLoadingQuery(query, handleQueryError, addOnlineEventListener, removeOnlineEventListener, {
        ...options,
        skip: reachability === 'service-unavailable' || options?.skip
    } as LoadingQueryOptions<TData, TVariables, TSubscriptionData, TSubscriptionVariables>);
}
